import classNames from 'classnames';
import React, { useState, useRef, DragEvent, ChangeEvent, useEffect } from 'react';
import { CircleIconButton } from 'ui/CircleIconButton';
import XIcon from 'ui/Icons/tabler/x.component.svg';

interface UseFilesInputOptions {
  multiple?: boolean;
  accept?: string;
  onChange?: (files: File[]) => void;
}

interface FilePreview {
  file: File;
  previewUrl: string;
}

interface UseFilesInputReturn {
  files: File[];
  previews: FilePreview[];
  isDragging: boolean;
  renderInput: () => React.ReactElement;
  clearFiles: () => void;
  addFiles: (newFiles: File[]) => void;
  removeFile: (index: number) => void;
  reset: () => void;
}

export const useFilesInput = (options: UseFilesInputOptions = {}): UseFilesInputReturn => {
  const { multiple = false, accept = '*', onChange } = options;

  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<FilePreview[]>([]);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Clean up URLs to prevent memory leaks when component unmounts
    return () => {
      previews.forEach(preview => URL.revokeObjectURL(preview.previewUrl));
    };
  }, []);

  const processFiles = (newFiles: File[]) => {
    // If not multiple, replace existing files
    const updatedFiles = multiple ? [...files, ...newFiles] : newFiles;

    // Create previews for the new files
    const newPreviews = newFiles.map(file => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }));

    // If not multiple, clean up old preview URLs
    if (!multiple) {
      previews.forEach(preview => URL.revokeObjectURL(preview.previewUrl));
      setPreviews(newPreviews);
    } else {
      setPreviews([...previews, ...newPreviews]);
    }

    setFiles(updatedFiles);

    if (onChange) {
      onChange(updatedFiles);
    }
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = multiple ? Array.from(e.dataTransfer.files) : [e.dataTransfer.files[0]];

      processFiles(droppedFiles);
    }
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFiles = multiple ? Array.from(e.target.files) : [e.target.files[0]];

      processFiles(selectedFiles);
    }
  };

  const handleAreaClick = () => {
    if ((files.length === 0 || multiple) && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const clearFiles = () => {
    // Clean up all preview URLs
    previews.forEach(preview => URL.revokeObjectURL(preview.previewUrl));

    setFiles([]);
    setPreviews([]);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (onChange) {
      onChange([]);
    }
  };

  const removeFile = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);

    // Clean up the preview URL
    URL.revokeObjectURL(previews[index].previewUrl);

    const newPreviews = [...previews];
    newPreviews.splice(index, 1);

    setFiles(newFiles);
    setPreviews(newPreviews);

    // Reset the file input when removing a file so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (onChange) {
      onChange(newFiles);
    }
  };

  const addFiles = (newFiles: File[]) => {
    processFiles(newFiles);
  };

  const getFilePreview = (file: File, previewUrl: string, index: number) => {
    const isImage = file.type.startsWith('image/');
    const fileExt = file.name.split('.').pop();
    return (
      <div key={index} className="flex items-center justify-between p-10px">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {isImage ? (
            <img
              src={previewUrl}
              alt={file.name}
              style={{ maxWidth: '100px', maxHeight: '100px', marginRight: '10px' }}
            />
          ) : (
            <div className="w-[60px] h-[60px] bg-gray-40 flex items-center justify-center uppercase">{fileExt}</div>
          )}
          <div className="ml-4">
            <span>{file.name}</span>
            <span className="block text-12px">{(file.size / 1024).toFixed(2)} KB</span>
          </div>
        </div>
        <CircleIconButton
          type="secondary"
          className="bespoke-negotiation-edit-button ml-10px"
          disabled={false}
          IconComponent={XIcon}
          iconSize="default"
          onClick={e => {
            e.stopPropagation();
            removeFile(index);
          }}
        />
      </div>
    );
  };

  const renderInput = () => {
    return (
      <div>
        <div
          className={classNames(
            'cursor-pointer outline-none italic bg-brown-prime-10 text-gray-100 border-2 border-gray-40 text-center border-dashed p-4 text-sm',
            {
              'o:bg-gray-40': isDragging,
            }
          )}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={handleAreaClick}
        >
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
            multiple={multiple}
            accept={accept}
          />

          {files.length === 0 ? (
            <div className="font-pt-sans text-13px leading-21px tracking-2xs">
              <span className="block">Drag & drop a file here</span>
              <span className="block">
                or <span className="text-brown-80 underline">select from your computer</span>
              </span>
            </div>
          ) : (
            <div>
              {previews.map((preview, index) => getFilePreview(preview.file, preview.previewUrl, index))}

              {multiple && (
                <div className="flex justify-center items-center">
                  <button
                    className="font-pt-sans cursor-pointer text-13px leading-21px tracking-2xs underline text-brown-80 bg-transparent"
                    onClick={e => {
                      e.stopPropagation();
                      if (fileInputRef.current) {
                        fileInputRef.current.click();
                      }
                    }}
                  >
                    Add More Files
                  </button>

                  <button
                    className="font-pt-sans cursor-pointer text-13px leading-21px tracking-2xs underline text-brown-80 bg-transparent"
                    onClick={e => {
                      e.stopPropagation();
                      clearFiles();
                    }}
                  >
                    Remove All
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const reset = () => {
    // reset the whole input (e.g if this component is used in a modal and we close the modal)
    clearFiles();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return {
    files,
    previews,
    isDragging,
    renderInput,
    clearFiles,
    addFiles,
    removeFile,
    reset,
  };
};
